/**
 * features/Modal/ui/AddTechCard.jsx
 *
 * Модальное окно создания тех. карты.
 * Добавлены поля: card_mode, warehouse_from_id, warehouse_to_id, output_items.
 */
import React, { useContext, useEffect, useState } from "react";
import {
  Form, Modal, Select, Switch, InputNumber, Button,
  Divider, Space, message, Tooltip,
} from "antd";
import { PlusOutlined, DeleteOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { TechCardContext } from "src/shared/lib/hooks/context/getTechCardsContext";
import { apiRequests } from "src/shared/api/api";

const { Option } = Select;



const CARD_MODE_OPTIONS = [
  {
    value:       "reference",
    label:       "Справочный",
    description: "Только список компонентов, никакой автоматизации",
  },
  {
    value:       "semi_auto",
    label:       "Полуавтомат",
    description: "При продаже автоматически списывается сырьё со склада",
  },
  {
    value:       "auto",
    label:       "Автомат",
    description: "При продаже создаётся тех. операция: списание + производство",
  },
];



export default function AddTechCard({ isOpen, setOpen, onSuccess, refreshData: refreshDataProp }) {
  const { token, refreshData: ctxRefresh } = useContext(TechCardContext);
  const refreshData = refreshDataProp || ctxRefresh;

  const [form]        = Form.useForm();
  const [loading, setLoading]       = useState(false);
  const [cardMode, setCardMode]     = useState("reference");
  const [isDirty, setIsDirty]       = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);

  
  const [nomenclatureOptions, setNomenclatureOptions] = useState([]);
  const [warehouseOptions, setWarehouseOptions]       = useState([]);

  useEffect(() => {
    if (!isOpen) { form.resetFields(); setCardMode("reference"); setIsDirty(false); return; }
    loadOptions(); // eslint-disable-next-line
  }, [isOpen]);

  const loadOptions = async () => {
    try {
      const [nomRes, whRes] = await Promise.all([
        apiRequests.nomenclature.getItems({ token, limit: 500 }),
        apiRequests.warehouses.getItems({ token }),
      ]);
      const nomRaw = Array.isArray(nomRes.data) ? nomRes.data : (nomRes.data?.result || []);
      const whRaw  = Array.isArray(whRes.data)  ? whRes.data  : (whRes.data?.result  || []);
      setNomenclatureOptions(nomRaw.map(n => ({ value: n.id, label: n.name })));
      setWarehouseOptions(whRaw.map(w => ({ value: w.id, label: w.name })));
    } catch (e) {
      console.error("loadOptions error:", e);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        name:                 values.name,
        description:          values.description || "",
        card_type:            "automatic",          
        card_mode:            values.card_mode,
        auto_produce:         values.auto_produce || false,
        parent_nomenclature_id: values.parent_nomenclature_id || null,
        warehouse_from_id:    values.warehouse_from_id || null,
        warehouse_to_id:      values.warehouse_to_id   || null,
        items: (values.items || []).map(item => ({
          nomenclature_id:             parseInt(item.nomenclature_id),
          type_of_processing:          item.type_of_processing || null,
          quantity:                    parseFloat(item.quantity || 1),
          net_weight:                  parseFloat(item.net_weight || 0),
          gross_weight:                parseFloat(item.gross_weight || 0),
          output:                      parseFloat(item.output || 0),
          waste_from_cold_processing:  parseFloat(item.waste_from_cold_processing || 0),
          waste_from_heat_processing:  parseFloat(item.waste_from_heat_processing || 0),
        })).filter(i => i.nomenclature_id),
        output_items: (values.output_items || []).map(item => ({
          nomenclature_id: parseInt(item.nomenclature_id),
          quantity:        parseFloat(item.quantity || 1),
          unit_id:         item.unit_id ? parseInt(item.unit_id) : null,
        })).filter(i => i.nomenclature_id),
      };

      
      if (!payload.items.length) {
        message.error("Добавьте хотя бы один компонент (сырьё)");
        return;
      }
      if (values.card_mode === "auto" && !payload.warehouse_to_id) {
        message.error("Для режима «Автомат» нужно указать склад продукта");
        return;
      }

      await apiRequests.tech_cards.post(payload);
      message.success("Тех. карта создана");
      form.resetFields();
      setOpen(false);
      if (refreshData) await refreshData();
      if (onSuccess)   onSuccess();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      message.error(
        typeof detail === "string" ? detail : "Ошибка при создании тех. карты"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (isDirty) { setConfirmClose(true); return; }
    setOpen(false);
  };

  const showWarehouses = cardMode === "semi_auto" || cardMode === "auto";

  return (
    <>
      <Modal
        open={isOpen}
        title="Добавить тех. карту"
        width={860}
        onCancel={handleCancel}
        onOk={() => form.submit()}
        okText="Создать"
        cancelText="Отмена"
        confirmLoading={loading}
        destroyOnClose
        maskClosable={false}
      >
        <Form
          form={form}
          layout="vertical"
          onValuesChange={() => setIsDirty(true)}
          onFinish={handleSubmit}
          initialValues={{ card_mode: "reference", auto_produce: false }}
        >
          {/* ── Основные поля ──────────────────────────────────────────── */}
          <Form.Item
            name="name"
            label="Название"
            rules={[{ required: true, message: "Введите название" }]}
          >
            <input
              className="ant-input p-2 border-gray/50 border rounded-sm"
              placeholder="Например: Тесто пшеничное"
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item name="description" label="Описание">
            <textarea className="ant-input p-2 border-gray/50 border rounded-sm w-[50%]" rows={2} />
          </Form.Item>

          {/* ── Режим ──────────────────────────────────────────────────── */}
          <Form.Item
            name="card_mode"
            label={
              <Space>
                Режим тех. карты
                <Tooltip title="Определяет, что произойдёт автоматически при создании продажи">
                  <InfoCircleOutlined style={{ color: "#888" }} />
                </Tooltip>
              </Space>
            }
            rules={[{ required: true }]}
          >
            <Select onChange={setCardMode} style={{ width: "100%" }}>
              {CARD_MODE_OPTIONS.map(opt => (
                <Option key={opt.value} value={opt.value}>
                  <Space>
                    <strong>{opt.label}</strong>
                    <span style={{ color: "#888", fontSize: 12 }}>— {opt.description}</span>
                  </Space>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="auto_produce" label="Автопроизводство" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item name="parent_nomenclature_id" label="Номенклатура (что производим)">
            <Select
              showSearch
              allowClear
              placeholder="Выберите номенклатуру"
              options={nomenclatureOptions}
              optionFilterProp="label"
              style={{ width: "100%" }}
            />
          </Form.Item>

          {/* ── Склады (только для semi_auto / auto) ───────────────────── */}
          {showWarehouses && (
            <>
              <Divider>Склады</Divider>
              <Form.Item
                name="warehouse_from_id"
                label="Склад сырья (откуда берём)"
                rules={[{ required: true, message: "Укажите склад сырья" }]}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Выберите склад"
                  options={warehouseOptions}
                  optionFilterProp="label"
                  style={{ width: "100%" }}
                />
              </Form.Item>

              {cardMode === "auto" && (
                <Form.Item
                  name="warehouse_to_id"
                  label="Склад продукта (куда идёт готовое)"
                  rules={[{ required: true, message: "Укажите склад продукта" }]}
                >
                  <Select
                    showSearch
                    allowClear
                    placeholder="Выберите склад"
                    options={warehouseOptions}
                    optionFilterProp="label"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              )}
            </>
          )}

          {/* ── Компоненты (сырьё) ─────────────────────────────────────── */}
          <Divider>Компоненты (сырьё)</Divider>
          <Form.List name="items">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <div
                    key={key}
                    style={{
                      display:       "grid",
                      gridTemplateColumns: "2fr 1fr 1fr auto",
                      gap:           8,
                      marginBottom:  8,
                      padding:       "8px 12px",
                      background:    "#fafafa",
                      borderRadius:  6,
                      border:        "1px solid #f0f0f0",
                    }}
                  >
                    <Form.Item
                      {...restField}
                      name={[name, "nomenclature_id"]}
                      rules={[{ required: true, message: "Выберите номенклатуру" }]}
                      style={{ margin: 0 }}
                    >
                      <Select
                        showSearch
                        className="max-w-[100%]"
                        placeholder="Номенклатура (сырьё)"
                        options={nomenclatureOptions}
                        optionFilterProp="label"
                      />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "quantity"]}
                      rules={[{ required: true }]}
                      style={{ margin: 0 }}
                    >
                      <InputNumber
                        min={0.001}
                        step={0.1}
                        placeholder="Кол-во"
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "type_of_processing"]}
                      style={{ margin: 0 }}
                    >
                      <input
                        className="ant-input p-1"
                        placeholder="Тип обработки"
                      />
                    </Form.Item>
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => remove(name)}
                    />
                  </div>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add()}
                  icon={<PlusOutlined />}
                  block
                >
                  Добавить компонент
                </Button>
              </>
            )}
          </Form.List>

          {/* ── Выходные изделия (только для auto / semi_auto) ─────────── */}
          {showWarehouses && (
            <>
              <Divider>Выходные изделия (что производим)</Divider>
              <Form.List name="output_items">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <div
                        key={key}
                        style={{
                          display:             "grid",
                          gridTemplateColumns: "2fr 1fr auto",
                          gap:                 8,
                          marginBottom:        8,
                          padding:             "8px 12px",
                          background:          "#f6ffed",
                          borderRadius:        6,
                          border:              "1px solid #b7eb8f",
                        }}
                      >
                        <Form.Item
                          {...restField}
                          name={[name, "nomenclature_id"]}
                          rules={[{ required: true, message: "Выберите номенклатуру" }]}
                          style={{ margin: 0 }}
                        >
                          <Select
                            showSearch
                            placeholder="Номенклатура (готовый продукт)"
                            options={nomenclatureOptions}
                            optionFilterProp="label"
                          />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, "quantity"]}
                          rules={[{ required: true }]}
                          style={{ margin: 0 }}
                        >
                          <InputNumber
                            min={0.001}
                            step={0.1}
                            placeholder="Кол-во на выходе"
                            style={{ width: "100%" }}
                          />
                        </Form.Item>
                        <Button
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(name)}
                        />
                      </div>
                    ))}
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      icon={<PlusOutlined />}
                      block
                      style={{ borderColor: "#52c41a", color: "#52c41a" }}
                    >
                      Добавить выходное изделие
                    </Button>
                  </>
                )}
              </Form.List>
            </>
          )}
        </Form>
      </Modal>

      {/* Подтверждение закрытия с несохранёнными данными */}
      <Modal
        open={confirmClose}
        title="Закрыть без сохранения?"
        onOk={() => { setConfirmClose(false); setIsDirty(false); setOpen(false); form.resetFields(); }}
        onCancel={() => setConfirmClose(false)}
        okText="Да, закрыть"
        cancelText="Отмена"
        centered
      >
        <p>Изменения не будут сохранены.</p>
      </Modal>
    </>
  );
}